import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";
import apiClient from "../services/apiClient";

// Create base store configuration for attachments
const attachmentsBaseStore = createBaseStoreRefactor({
  endpoint: "/api/v1/attachments",
  name: "AttachmentsStoreRefactor",
  objectName: "Bijlage", // Auto-generates CRUD success messages
  enableCache: true, // Enable caching for attachment records
  actionCableChannel: null, // No real-time updates needed
  infiniteLoading: false, // Simple list, no pagination needed
});

export const useAttachmentsStoreRefactor = defineStore("attachmentsRefactor", {
  ...attachmentsBaseStore,

  state: () => ({
    ...attachmentsBaseStore.state(),
    // Additional state for attachment-specific functionality
    recordData: {}, // Track which record types and IDs we've already fetched
    cacheTimeout: 5 * 60 * 1000, // 5 minutes cache timeout
  }),

  getters: {
    ...attachmentsBaseStore.getters,

    /**
     * Get all attachments (maps to state.data for compatibility)
     */
    attachments: (state) => {
      return state.data || [];
    },

    /**
     * Get attachments for a specific record
     * Only returns actual attachments (name: "attachments"), not profile images
     */
    getAttachmentsForRecord: (state) => (recordType, recordId) => {
      return state.data.filter(
        (att) =>
          att.record_type === recordType &&
          att.record_id === recordId &&
          att.name === "attachments",
      );
    },

    /**
     * Check if a specific record's attachments need to be refreshed
     */
    shouldRefetchForRecord: (state) => (recordType, recordId) => {
      const key = `${recordType}-${recordId}`;

      if (!state.recordData[key]) return true;

      const timeSinceFetch = Date.now() - state.recordData[key].lastFetched;
      return timeSinceFetch > state.cacheTimeout;
    },

    /**
     * Get attachment by ID
     */
    getAttachmentById: (state) => (id) => {
      return state.data.find((attachment) => attachment.id == id);
    },
  },

  actions: {
    ...attachmentsBaseStore.actions,

    /**
     * Get upload URL for image uploads
     */
    async getUploadUrl() {
      try {
        console.log("AttachmentsStoreRefactor: Getting upload URL from API...");
        const response = await apiClient.get("/api/v1/images/upload_url");
        console.log("AttachmentsStoreRefactor: Upload URL response:", response);
        return response;
      } catch (error) {
        console.error(
          "AttachmentsStoreRefactor: Failed to get upload URL:",
          error,
        );
        this.error = error.message || "Failed to get upload URL";
        throw error;
      }
    },

    /**
     * Create attachment record after successful S3 upload
     */
    async createAttachment(recordType, recordId, field, s3Response) {
      try {
        console.log(
          "AttachmentsStoreRefactor: Saving attachment record to database...",
        );

        const attachmentData = {
          record_type: recordType,
          record_id: recordId,
          field: field,
          s3_response: s3Response,
        };

        const response = await apiClient.post(
          "/api/v1/attachments",
          attachmentData,
        );

        console.log("AttachmentsStoreRefactor: Server response:", response);
        console.log(
          "AttachmentsStoreRefactor: Store data before upsert:",
          this.data.length,
        );

        // Add the new attachment to our store using the inherited method
        this._upsertItem(response);

        console.log(
          "AttachmentsStoreRefactor: Store data after upsert:",
          this.data.length,
        );
        console.log(
          "AttachmentsStoreRefactor: New attachment in store:",
          this.data.find((item) => item.id === response.id),
        );

        // Clear cache for this record since we added a new attachment
        const requestKey = `${recordType}-${recordId}`;
        delete this.recordData[requestKey];

        console.log(
          "AttachmentsStoreRefactor: Attachment record saved successfully",
        );
        return response;
      } catch (error) {
        console.error(
          "AttachmentsStoreRefactor: Failed to save attachment record:",
          error,
        );
        this.error = error.message || "Failed to save attachment record";

        // Check for specific server errors and provide better error messages
        if (error.response?.data?.message?.includes("redis")) {
          throw new Error(
            "Server configuratiefout: Redis database niet beschikbaar. Neem contact op met de beheerder.",
          );
        } else if (error.message?.includes("Connection refused")) {
          throw new Error(
            "Server database fout. Het bestand is geüpload naar S3, maar de database record kon niet worden opgeslagen. Neem contact op met de beheerder.",
          );
        } else {
          throw new Error(
            `Er is een fout opgetreden bij het uploaden van het bestand: ${error.message || error}`,
          );
        }
      }
    },

    /**
     * Delete attachment with custom success message
     */
    async delete(attachmentId) {
      try {
        await this.destroy(attachmentId);
        return { success: true };
      } catch (error) {
        this.error = error.message || "Failed to delete attachment";
        console.error(
          "AttachmentsStoreRefactor: Error deleting attachment:",
          error,
        );
        throw error;
      }
    },

    /**
     * Fetch attachments for a specific record - simplified version
     */
    async fetchByRecord(recordType, recordId) {
      try {
        const response = await this.fetchAll({
          record_type: recordType,
          record_id: recordId,
        });

        // Filter out only actual attachments (exclude profile images, etc.)
        const actualAttachments = response.filter(
          (item) => item.name === "attachments",
        );
        console.log(
          `AttachmentsStoreRefactor: Filtered to ${actualAttachments.length} actual attachments for ${recordType} ${recordId}`,
        );

        // Update the record data to track when we last fetched
        const requestKey = `${recordType}-${recordId}`;
        this.recordData[requestKey] = {
          lastFetched: Date.now(),
          count: actualAttachments.length,
        };

        return actualAttachments;
      } catch (error) {
        this.error = error.message || "Failed to fetch attachments";
        console.error(
          "AttachmentsStoreRefactor: Error in fetchByRecord:",
          error,
        );
        this.loading = false;
        return [];
      }
    },

    /**
     * Override addOrUpdate to add attachment to store
     */
    addOrUpdate(entity) {
      this._upsertItem(entity);
    },

    /**
     * Override remove to remove attachment from store
     */
    remove(id) {
      this._removeItem(id);
    },
  },
});
